import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * Orca runner status — lifecycle states for a build runner in the pool.
 * @since 1.0.0
 */
export const OrcaRunnerStatus = Schema.Literal(
  "PENDING",
  "ONLINE",
  "DRAINING",
  "OFFLINE",
);
export type OrcaRunnerStatus = Schema.Schema.Type<typeof OrcaRunnerStatus>;

/**
 * OrcaRunner model — one row per build runner (e.g. a provisioned Mac) in the
 * pool that executes orca tasks. The schema supports a pool; we provision a
 * single Mac for now.
 *
 * @since 1.0.0
 */
export class OrcaRunner extends Model.Class<OrcaRunner>("OrcaRunner")({
  id: Model.Generated(Schema.String),

  name: Schema.String,
  status: OrcaRunnerStatus,

  runnerToken: Schema.String,
  capacity: Schema.Number,
  labels: Schema.Array(Schema.String),

  /**
   * OL-33: the customer this runner is dedicated to; `null` = unowned shared
   * capacity. Placement filters dispatch on this so a customer's spawn never
   * lands on another customer's runner.
   *
   * @since 1.0.0
   */
  customerId: Schema.NullOr(Schema.String),

  lastHeartbeatAt: Schema.NullOr(Schema.DateTimeUtc),
  connectedAt: Schema.NullOr(Schema.DateTimeUtc),

  /**
   * OL-128: whether this daemon is actually consuming work.
   * `commandsConsumed` is a monotonic count of command-stream frames consumed
   * (never rewound by a restart reporting a smaller value);
   * `lastCommandConsumedAt` is when that count last advanced — `null` until it
   * advances for the first time. Read together, a runner that heartbeats while
   * consuming nothing is distinguishable from a healthy idle one.
   *
   * @since 1.0.0
   */
  commandsConsumed: Schema.Number,
  lastCommandConsumedAt: Schema.NullOr(Schema.DateTimeUtc),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
